import {
    BusinessLineEventBlueprintField,
    DefaultMeetingDurationEventBlueprintField,
    EventAccessEventBlueprintField,
    EventBlueprintAction,
    EventBlueprintField,
    ICreateEventBlueprintDTO,
    IEventBlueprintDTO,
    IntegrationMeetingTemplateIdEventBlueprintField,
    MeetingTemplateIdEventBlueprintField,
    PassportConfigEventBlueprintField,
    PassportSiteAction,
    PassportUrlFormatBlueprintField,
    RegionEventBlueprintField,
    SingleMeetingAction,
    TimeZoneEventBlueprintField,
    TypeEventBlueprintField
} from '../../../../api/blueprints/event.blueprint.types';
import { BlueprintType } from '../../../../api/blueprints/blueprints.dao';
import { ActionType, BlueprintFieldType } from '../../../../api/blueprints/blueprint.types';
import { FieldKey } from './event-blueprint-form.config';
import { S3ObjectMetadata } from '../../../../api/shared.dto';
import { TEventTypeLabel } from '../../../../api/events.dao';
import { AttendeeLinkType } from '../../../../api/attendees.dao';

export enum ParticipantLinkType {
    PASSPORT_LINK = 'PASSPORT_LINK',
    MEETING_LINK_WITH_EMBEDDED_PASSCODE = 'MEETING_LINK_WITH_EMBEDDED_PASSCODE'
}

export type Values = { [key: string]: unknown };

export interface EventBlueprintValues extends Values {
    accountId: number;
    name: string;
    description?: string;
    createdAt?: string;
    blueprintId?: string;
    integrationEnabled: boolean;
    eventTypes: TEventTypeLabel[];
    attendeeLinkType: AttendeeLinkType;
    orderBookingEnabled?: boolean;
    publicName?: string;
    publicDescription?: string;
    participantLinkType?: string;
    orderBookingSubset?: string[];
    defaultMeetingBlueprintId?: string;
    eventAttributeIds?: number[];
    externalId?: string;
}

export class EventBlueprintMapper {
    public static toEventBlueprintDTO(values: EventBlueprintValues): ICreateEventBlueprintDTO {
        const dto = { type: BlueprintType.EVENT } as ICreateEventBlueprintDTO;

        dto.accountId = values.accountId;
        dto.name = values[FieldKey.NAME].trim();
        dto.description = values[FieldKey.DESCRIPTION].trim();
        dto.integrationEnabled = values.integrationEnabled;
        dto.eventTypes = values.eventTypes;
        const externalId = values[FieldKey.EXTERNAL_ID]?.trim();
        dto.externalId = externalId || null;

        if (values.attendeeLinkType) dto.attendeeLinkType = values.attendeeLinkType;
        if (values.defaultMeetingBlueprintId) dto.defaultMeetingBlueprintId = values.defaultMeetingBlueprintId;

        dto.orderBookingEnabled = Boolean(values[FieldKey.ORDER_BOOKING_ENABLED]);
        dto.orderBookingSubset = values[FieldKey.ORDER_BOOKING_SUBSET];
        if (dto.orderBookingEnabled) {
            const publicName = values[FieldKey.PUBLIC_NAME];
            if (publicName) dto.publicName = publicName;

            const publicDescription = values[FieldKey.PUBLIC_DESCRIPTION];
            if (publicDescription) dto.publicDescription = publicDescription;

            const participantLinkType = values[FieldKey.PARTICIPANT_LINK_TYPE];
            if (participantLinkType) dto.participantLinkType = participantLinkType;

            dto.eventAttributeIds = Array.isArray(values[FieldKey.EVENT_ATTRIBUTE_IDS] ?? values.eventAttributeIds)
                ? (values[FieldKey.EVENT_ATTRIBUTE_IDS] ?? values.eventAttributeIds).filter(id => !isNaN(id))
                : [];

        }

        dto.fields = EventBlueprintMapper.toEventBlueprintFields(values);
        dto.actions = EventBlueprintMapper.toEventBlueprintActions(values);

        return dto;
    }

    public static toEditEventBlueprintDTO(values: EventBlueprintValues): IEventBlueprintDTO {
        const dto = EventBlueprintMapper.toEventBlueprintDTO(values);

        dto.blueprintId = values.blueprintId;
        dto.createdAt = values.createdAt;

        return dto;
    }

    public static toEventBlueprintValuesFromEventBlueprintDTO(dto: IEventBlueprintDTO): EventBlueprintValues {
        const values = {} as EventBlueprintValues;

        values.accountId = dto.accountId;
        values.name = dto.name;
        values.description = dto.description;
        values.integrationEnabled = dto.integrationEnabled;
        values.eventTypes = dto.eventTypes;
        values[FieldKey.EXTERNAL_ID] = dto.externalId || '';
        values.attendeeLinkType = dto.attendeeLinkType;
        values.orderBookingEnabled = dto.orderBookingEnabled;
        values.publicName = dto.publicName;
        values.publicDescription = dto.publicDescription;
        values.participantLinkType = dto.participantLinkType;
        values.orderBookingSubset = dto.orderBookingSubset;
        values.defaultMeetingBlueprintId = dto.defaultMeetingBlueprintId;
        values.eventAttributeIds = dto.eventAttributeIds || [];

        /* [OEP-5186] manually set attendeeLinkType to default for the blueprints with enabled integration created in the past */
        if (dto.integrationEnabled && !dto.attendeeLinkType) {
            values.attendeeLinkType = AttendeeLinkType.MEETING_LINK_WITH_EMBEDDED_PASSCODE;
        }

        dto.fields?.forEach(field => {
            values[field.name] = field.value;
        });

        dto.actions?.forEach(action => {
            const correctActionKey = EventBlueprintMapper.actionMapRelations.get(action.type);
            values[correctActionKey as string] = true;
        });

        return values;
    }

    private static toEventBlueprintFields(values: EventBlueprintValues): EventBlueprintField[] {
        return EventBlueprintMapper.fieldMapperList
            .reduce((fields, mapper) => {
                const field = mapper(values);
                return field ? fields.concat(field) : fields;
            }, [] as EventBlueprintField[]);
    }

    private static toEventBlueprintActions(values: EventBlueprintValues): EventBlueprintAction[] {
        return EventBlueprintMapper.actionMapperList
            .reduce((actions, mapper) => {
                const action = mapper(values);
                return action ? actions.concat(action) : actions;
            }, [] as EventBlueprintAction[]);
    }

    private static get fieldMapperList(): ((values: EventBlueprintValues) => EventBlueprintField | null)[] {
        return [
            EventBlueprintMapper.mapTimeZoneField,
            EventBlueprintMapper.mapEventRegionField,
            EventBlueprintMapper.mapEventBusinessLineField,
            EventBlueprintMapper.mapTypeEventField,
            EventBlueprintMapper.mapEventAccessField,
            EventBlueprintMapper.mapDefaultMeetingDurationField,
            EventBlueprintMapper.mapMeetingTemplateField,
            EventBlueprintMapper.mapPassportConfigField,
            EventBlueprintMapper.mapPassportUrlFormatField,
            EventBlueprintMapper.mapIntegrationMeetingTemplateField
        ];
    }

    private static get actionMapperList(): ((values: EventBlueprintValues) => EventBlueprintAction | null)[] {
        return [
            EventBlueprintMapper.mapCreatePassportSiteAction,
            EventBlueprintMapper.mapCreateSingleMeetingAction
        ];
    }

    private static get actionMapRelations(): Map<ActionType, FieldKey> {
        return new Map([
            [ActionType.PASSPORT_SITE, FieldKey.CREATE_PASSPORT_SITE],
            [ActionType.SINGLE_MEETING, FieldKey.CREATE_SINGLE_MEETING]
        ]);
    }

    private static mapTimeZoneField(values: EventBlueprintValues): TimeZoneEventBlueprintField | null {
        if (!values[FieldKey.TIME_ZONE]) return null;
        return { name: 'timeZone', value: values[FieldKey.TIME_ZONE] as string, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapEventRegionField(values: EventBlueprintValues): RegionEventBlueprintField | null {
        if (!values[FieldKey.REGION]) return null;
        return { name: 'region', value: values[FieldKey.REGION] as string, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapEventBusinessLineField(values: EventBlueprintValues): BusinessLineEventBlueprintField | null {
        if (!values[FieldKey.BUSINESS_LINE]) return null;
        return { name: 'businessLine', value: values[FieldKey.BUSINESS_LINE] as string, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapTypeEventField(values: EventBlueprintValues): TypeEventBlueprintField | null {
        if (!values[FieldKey.TYPE]) return null;
        return { name: 'type', value: values[FieldKey.TYPE] as string, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapEventAccessField(values: EventBlueprintValues): EventAccessEventBlueprintField | null {
        if (!values[FieldKey.EVENT_ACCESS]) return null;
        return { name: 'eventAccess', value: values[FieldKey.EVENT_ACCESS] as string, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapDefaultMeetingDurationField(values: EventBlueprintValues): DefaultMeetingDurationEventBlueprintField | null {
        if (!values[FieldKey.DEFAULT_MEETING_DURATION]) return null;
        return { name: 'defaultMeetingDuration', value: values[FieldKey.DEFAULT_MEETING_DURATION] as number, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapMeetingTemplateField(values: EventBlueprintValues): MeetingTemplateIdEventBlueprintField | null {
        if (!values[FieldKey.MEETING_TEMPLATE]) return null;
        return { name: 'meetingBlueprintId', value: values[FieldKey.MEETING_TEMPLATE] as string, type: BlueprintFieldType.SINGLE_MEETING_ACTION_INPUT };
    }

    private static mapPassportConfigField(values: EventBlueprintValues): PassportConfigEventBlueprintField | null {
        if (!values[FieldKey.PASSPORT_CONFIG]) return null;
        return { name: 'passportConfig', value: values[FieldKey.PASSPORT_CONFIG] as S3ObjectMetadata, type: BlueprintFieldType.PASSPORT_SITE_ACTION_INPUT };
    }

    private static mapPassportUrlFormatField(values: EventBlueprintValues): PassportUrlFormatBlueprintField | null {
        if (!values[FieldKey.PASSPORT_URL_FORMAT]) return null;
        return { name: 'passportUrlFormat', value: values[FieldKey.PASSPORT_URL_FORMAT] as string, type: BlueprintFieldType.PASSPORT_SITE_ACTION_INPUT };
    }

    private static mapCreatePassportSiteAction(values: EventBlueprintValues): PassportSiteAction | null {
        if (!values[FieldKey.CREATE_PASSPORT_SITE]) return null;
        return { order: 0, type: ActionType.PASSPORT_SITE };
    }
    private static mapCreateSingleMeetingAction(values: EventBlueprintValues): SingleMeetingAction | null {
        if (!values[FieldKey.CREATE_SINGLE_MEETING]) return null;
        return { order: 1, type: ActionType.SINGLE_MEETING };
    }

    private static mapIntegrationMeetingTemplateField(values: EventBlueprintValues): IntegrationMeetingTemplateIdEventBlueprintField | null {
        if (!values[FieldKey.INTEGRATION_MEETING_TEMPLATE]) return null;
        return { name: 'integrationMeetingBlueprintId', value: values[FieldKey.INTEGRATION_MEETING_TEMPLATE] as string, type: BlueprintFieldType.FORM_PREFILL };
    }
}
